// src/components/Features/ExecutiveTraining.tsx - Executive leadership training LMS for Premium users
import React, { useState, useEffect, useCallback } from 'react';
import SubscriptionUpgradeModal from '../SubscriptionUpgradeModal';
import {
  trainingService,
  LessonMediaItem,
  TrainingProgressOverview,
  TrainingModuleSummary,
  TrainingModuleDetail,
  QuizSubmitResponse,
  TrainingCertificate,
} from '../../services/trainingService';

interface ExecutiveTrainingProps {
  isOpen: boolean;
  onClose: () => void;
}

// Inline Markdown & Structured Content Renderer for Curriculum
export const renderFormattedText = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*|`.*?`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-bold text-gray-900">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="px-1.5 py-0.5 bg-gray-100 rounded text-purple-800 font-mono text-xs">
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
};

export const LessonMediaBlock: React.FC<{ media: LessonMediaItem[] }> = ({ media }) => {
  if (!media.length) return null;

  return (
    <div className="space-y-4">
      {media.map((item) => {
        const src = trainingService.mediaUrl(item.url);
        if (item.type === 'image') {
          return (
            <figure key={item.asset_id} className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
              <img
                src={src}
                alt={item.alt_text || item.caption || item.filename || 'Lesson illustration'}
                loading="lazy"
                className="w-full object-contain"
              />
              {(item.caption || item.alt_text) && (
                <figcaption className="px-4 py-2 text-xs text-gray-500">
                  {item.caption || item.alt_text}
                </figcaption>
              )}
            </figure>
          );
        }

        if (item.type === 'video') {
          return (
            <figure key={item.asset_id} className="overflow-hidden rounded-xl border border-gray-200 bg-black">
              <video controls preload="metadata" src={src} className="w-full">
                Your browser cannot play this lesson video.
              </video>
              {item.caption && (
                <figcaption className="bg-white px-4 py-2 text-xs text-gray-500">
                  {item.caption}
                </figcaption>
              )}
            </figure>
          );
        }

        return (
          <a
            key={item.asset_id}
            href={src}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between gap-3 rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 text-xs font-semibold text-purple-800 hover:bg-purple-100"
          >
            <span className="truncate">{item.caption || item.filename || 'Download lesson resource'}</span>
            <span aria-hidden="true">↓</span>
          </a>
        );
      })}
    </div>
  );
};

export const LessonContentRenderer: React.FC<{ markdown: string }> = ({ markdown }) => {
  const lines = markdown.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced Code / Diagram Block (e.g. ``` ... ```)
    if (line.trim().startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      elements.push(
        <pre
          key={`code-${i}`}
          className="my-4 bg-slate-900 text-slate-100 p-4 rounded-xl font-mono text-xs overflow-x-auto shadow-inner leading-relaxed border border-slate-800"
        >
          {codeLines.join('\n')}
        </pre>
      );
      continue;
    }

    // Markdown Table Detection (| header | header |)
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }

      if (tableLines.length >= 2) {
        const rawHeaders = tableLines[0].split('|').slice(1, -1).map((s) => s.trim());
        const bodyLines = tableLines.slice(1).filter((l) => !l.includes('---'));
        const rows = bodyLines.map((row) =>
          row.split('|').slice(1, -1).map((s) => s.trim())
        );

        elements.push(
          <div key={`table-${i}`} className="overflow-x-auto my-4 border border-gray-200 rounded-xl shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50 text-gray-700 font-semibold uppercase tracking-wider">
                <tr>
                  {rawHeaders.map((header, hIdx) => (
                    <th key={hIdx} className="px-4 py-2.5 text-left">
                      {renderFormattedText(header)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((row, rIdx) => (
                  <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className="px-4 py-2.5 text-gray-700">
                        {renderFormattedText(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // Headings
    if (line.startsWith('#### ')) {
      elements.push(
        <h5 key={`h4-${i}`} className="text-sm font-bold text-gray-900 uppercase tracking-wider mt-5 mb-2">
          {renderFormattedText(line.replace('#### ', ''))}
        </h5>
      );
      i++;
      continue;
    }

    if (line.startsWith('### ')) {
      elements.push(
        <h4 key={`h3-${i}`} className="text-lg font-bold text-gray-900 mt-5 mb-2 tracking-tight">
          {renderFormattedText(line.replace('### ', ''))}
        </h4>
      );
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      elements.push(
        <blockquote
          key={`quote-${i}`}
          className="border-l-4 border-purple-500 bg-purple-50/60 pl-4 py-2 my-3 italic text-gray-700 rounded-r-lg"
        >
          {renderFormattedText(line.replace('> ', ''))}
        </blockquote>
      );
      i++;
      continue;
    }

    // Unordered List (- item or * item)
    if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
      const listItems: string[] = [];
      while (
        i < lines.length &&
        (lines[i].trim().startsWith('- ') || lines[i].trim().startsWith('* '))
      ) {
        listItems.push(lines[i].trim().replace(/^[-*]\s+/, ''));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="space-y-1.5 my-3 pl-2 text-sm text-gray-700">
          {listItems.map((item, idx) => (
            <li key={idx} className="flex items-start">
              <span className="text-purple-600 font-bold mr-2">•</span>
              <span>{renderFormattedText(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered List (1. item)
    if (/^\d+\.\s+/.test(line.trim())) {
      const listItems: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        listItems.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="space-y-1.5 my-3 pl-2 text-sm text-gray-700">
          {listItems.map((item, idx) => (
            <li key={idx} className="flex items-start">
              <span className="text-purple-600 font-semibold mr-2">{idx + 1}.</span>
              <span>{renderFormattedText(item)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Standard Paragraph
    if (line.trim().length > 0) {
      elements.push(
        <p key={`p-${i}`} className="text-sm text-gray-700 leading-relaxed my-2">
          {renderFormattedText(line)}
        </p>
      );
    }

    i++;
  }

  return <div className="space-y-2">{elements}</div>;
};

const ExecutiveTraining: React.FC<ExecutiveTrainingProps> = ({ isOpen, onClose }) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Server-side entitlement source of truth
  const [serverAccess, setServerAccess] = useState<boolean | null>(null);

  // Data states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<TrainingProgressOverview | null>(null);

  // Active module player state
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null);
  const [activeModule, setActiveModule] = useState<TrainingModuleDetail | null>(null);
  const [loadingModule, setLoadingModule] = useState(false);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [isQuizMode, setIsQuizMode] = useState(false);

  // Quiz submission state
  const [userAnswers, setUserAnswers] = useState<Record<string, number>>({});
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState<QuizSubmitResponse | null>(null);

  // Certificate modal state
  const [certificate, setCertificate] = useState<TrainingCertificate | null>(null);
  const [showCertModal, setShowCertModal] = useState(false);
  const [loadingCert, setLoadingCert] = useState(false);
  const [markingLesson, setMarkingLesson] = useState(false);

  // Check server access on open
  useEffect(() => {
    if (isOpen) {
      trainingService.checkAccess().then((hasAccess) => {
        setServerAccess(hasAccess);
      });
    }
  }, [isOpen]);

  // Server-side entitlement is the single source of truth.
  const canAccess = serverAccess === true;

  // Load overview from backend
  const loadOverview = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    setError(null);
    try {
      const data = await trainingService.getOverview();
      setOverview(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load training modules. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [canAccess]);

  useEffect(() => {
    if (isOpen && canAccess) {
      loadOverview();
    }
  }, [isOpen, canAccess, loadOverview]);

  // Open a module
  const handleOpenModule = async (moduleId: string) => {
    setLoadingModule(true);
    setActiveModuleId(moduleId);
    setCurrentLessonIndex(0);
    setIsQuizMode(false);
    setUserAnswers({});
    setQuizResult(null);

    try {
      const detail = await trainingService.getModuleDetail(moduleId);
      setActiveModule(detail);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to load module details.');
      setActiveModuleId(null);
    } finally {
      setLoadingModule(false);
    }
  };

  // Back to catalog
  const handleBackToCatalog = () => {
    setActiveModuleId(null);
    setActiveModule(null);
    setIsQuizMode(false);
    setQuizResult(null);
    loadOverview();
  };

  // Select quiz answer
  const handleSelectAnswer = (questionId: string, optionIndex: number) => {
    setUserAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex,
    }));
  };

  // Submit quiz
  const handleSubmitQuiz = async () => {
    if (!activeModule) return;
    const unanswered = activeModule.quiz.filter((q) => userAnswers[q.id] === undefined);
    if (unanswered.length > 0) {
      alert(`Please answer all ${activeModule.quiz.length} questions before submitting.`);
      return;
    }

    setQuizSubmitting(true);
    try {
      const result = await trainingService.submitQuiz(activeModule.id, userAnswers);
      setQuizResult(result);
      if (result.passed) {
        trainingService.getOverview().then(setOverview).catch(() => {});
      }
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to submit quiz. Please try again.');
    } finally {
      setQuizSubmitting(false);
    }
  };

  // Save lesson-level progress, then refresh both the module and the catalog
  const handleMarkLessonComplete = async () => {
    if (!activeModule) return;
    const lesson = activeModule.lessons[currentLessonIndex];
    if (!lesson || lesson.is_completed) return;

    setMarkingLesson(true);
    try {
      await trainingService.completeLesson(activeModule.id, lesson.id);
      const [detail, refreshedOverview] = await Promise.all([
        trainingService.getModuleDetail(activeModule.id),
        trainingService.getOverview(),
      ]);
      setActiveModule(detail);
      setOverview(refreshedOverview);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to save lesson progress.');
    } finally {
      setMarkingLesson(false);
    }
  };

  // Load and show certificate
  const handleShowCertificate = async () => {
    setLoadingCert(true);
    try {
      const cert = await trainingService.getCertificate();
      setCertificate(cert);
      setShowCertModal(true);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Certificate not yet available. Please complete all modules.');
    } finally {
      setLoadingCert(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Scoped Print CSS for Certificate */}
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #printable-certificate-card,
          #printable-certificate-card * {
            visibility: visible !important;
          }
          #printable-certificate-card {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            margin: 0 !important;
            padding: 3rem !important;
            background: white !important;
            border: 8px solid #fef3c7 !important;
            box-shadow: none !important;
            z-index: 999999 !important;
          }
          .print-hide {
            display: none !important;
          }
        }
      `}</style>

      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-2 sm:p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-60 transition-opacity backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Main Modal Card */}
          <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900 text-white">
              <div className="flex items-center space-x-3">
                <div className="flex justify-center items-center w-11 h-11 bg-white bg-opacity-10 backdrop-blur rounded-xl border border-white border-opacity-20 shadow-inner">
                  <svg className="w-6 h-6 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-xl font-bold text-white tracking-wide">Flash Academy LMS</h2>
                    <span className="text-xs font-semibold uppercase px-2 py-0.5 rounded-full bg-purple-500 bg-opacity-30 border border-purple-300 text-purple-200">
                      ISO 22301:2019
                    </span>
                  </div>
                  <p className="text-xs text-purple-200">Executive Crisis Leadership & Business Continuity Certification</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-purple-200 hover:text-white p-2 rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors"
                title="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 bg-gray-50">
              {serverAccess === null ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                  <p className="text-sm text-gray-500 font-medium">Checking your training access...</p>
                </div>
              ) : !canAccess ? (
                /* Unentitled View */
                <div className="text-center py-12 px-4 max-w-lg mx-auto bg-white rounded-2xl shadow-sm border border-purple-100">
                  <div className="w-20 h-20 bg-gradient-to-tr from-purple-100 to-indigo-100 text-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-inner">
                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Premium Feature Required</h3>
                  <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                    Flash Academy's executive crisis leadership modules are included in the <strong>Premium Plan</strong> or accessible via the <strong>Executive Training add-on</strong>.
                  </p>
                  <div className="bg-purple-50 rounded-xl p-4 mb-6 text-left border border-purple-200">
                    <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wider mb-2">What you will unlock:</h4>
                    <ul className="text-xs text-purple-800 space-y-2">
                      <li className="flex items-center">
                        <svg className="w-4 h-4 text-purple-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        All modules on Incident Command, Out-of-Band Alerts & Crisis Governance
                      </li>
                      <li className="flex items-center">
                        <svg className="w-4 h-4 text-purple-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        Downloadable compliance records for annual cyber insurance renewals
                      </li>
                      <li className="flex items-center">
                        <svg className="w-4 h-4 text-purple-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        Official ISO 22301:2019 Crisis Leadership Certificate
                      </li>
                    </ul>
                  </div>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-indigo-700 shadow-md transition-all duration-200"
                  >
                    Upgrade Plan or Unlock Training
                  </button>
                </div>
              ) : activeModuleId ? (
                /* Course Player Mode */
                loadingModule || !activeModule ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                    <p className="text-sm text-gray-500 font-medium">Loading course lessons...</p>
                  </div>
                ) : (
                  <div className="flex flex-col h-full space-y-4">
                    {/* Player Navigation Header */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                      <button
                        onClick={handleBackToCatalog}
                        className="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-purple-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to All Modules
                      </button>
                      <div className="text-right">
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-100 text-purple-800">
                          {activeModule.category}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          {activeModule.iso_clause} • {activeModule.lessons_completed}/{activeModule.total_lessons} lessons
                        </span>
                      </div>
                    </div>

                    {/* Stepper Tabs */}
                    <div className="flex items-center space-x-2 overflow-x-auto pb-1">
                      {activeModule.lessons.map((les, idx) => (
                        <button
                          key={les.id}
                          onClick={() => {
                            setCurrentLessonIndex(idx);
                            setIsQuizMode(false);
                          }}
                          className={`flex-shrink-0 px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
                            !isQuizMode && currentLessonIndex === idx
                              ? 'bg-purple-600 text-white shadow-sm'
                              : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          {les.is_completed ? '✓ ' : ''}Lesson {idx + 1}: {les.title.length > 25 ? les.title.substring(0, 22) + '...' : les.title}
                        </button>
                      ))}
                      <button
                        onClick={() => setIsQuizMode(true)}
                        className={`flex-shrink-0 px-3.5 py-2 text-xs font-semibold rounded-lg transition-all flex items-center space-x-1 ${
                          isQuizMode
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100'
                        }`}
                      >
                        <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Final Knowledge Check
                      </button>
                    </div>

                    {/* Lesson Content Mode */}
                    {!isQuizMode && activeModule.lessons[currentLessonIndex] && (
                      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-6">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-purple-600">
                              Lesson {currentLessonIndex + 1} of {activeModule.lessons.length}
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                              ⏱ {activeModule.lessons[currentLessonIndex].duration}
                            </span>
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900">
                            {activeModule.lessons[currentLessonIndex].title}
                          </h3>
                        </div>

                        {/* Summary Callout */}
                        <div className="p-4 rounded-xl bg-purple-50 border-l-4 border-purple-600 text-purple-950 text-sm italic">
                          "{activeModule.lessons[currentLessonIndex].summary}"
                        </div>

                        {/* Structured Lesson Body using Custom Renderer */}
                        <div className="max-w-none text-gray-700 text-sm leading-relaxed">
                          <LessonContentRenderer
                            markdown={activeModule.lessons[currentLessonIndex].content_markdown}
                          />
                        </div>

                        <LessonMediaBlock
                          media={activeModule.lessons[currentLessonIndex].media || []}
                        />

                        {/* Key Takeaways */}
                        {activeModule.lessons[currentLessonIndex].takeaways.length > 0 && (
                          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                            <h4 className="text-xs font-bold text-emerald-900 uppercase tracking-wider mb-2 flex items-center">
                              <svg className="w-4 h-4 text-emerald-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                              </svg>
                              Key Executive Takeaways
                            </h4>
                            <ul className="text-xs text-emerald-900 space-y-1.5">
                              {activeModule.lessons[currentLessonIndex].takeaways.map((item, tIdx) => (
                                <li key={tIdx} className="flex items-start">
                                  <span className="text-emerald-500 font-bold mr-2">•</span>
                                  <span>{renderFormattedText(item)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Lesson Bottom Controls */}
                        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-100">
                          <button
                            disabled={currentLessonIndex === 0}
                            onClick={() => setCurrentLessonIndex((prev) => Math.max(0, prev - 1))}
                            className={`px-4 py-2 text-xs font-medium rounded-lg border ${
                              currentLessonIndex === 0
                                ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                                : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            ← Previous Lesson
                          </button>

                          <button
                            onClick={handleMarkLessonComplete}
                            disabled={
                              markingLesson ||
                              activeModule.lessons[currentLessonIndex].is_completed
                            }
                            className={`px-4 py-2 text-xs font-semibold rounded-lg border transition-colors ${
                              activeModule.lessons[currentLessonIndex].is_completed
                                ? 'text-emerald-700 border-emerald-200 bg-emerald-50 cursor-default'
                                : 'text-purple-700 border-purple-300 bg-purple-50 hover:bg-purple-100 disabled:opacity-50'
                            }`}
                          >
                            {activeModule.lessons[currentLessonIndex].is_completed
                              ? '✓ Lesson completed'
                              : markingLesson
                                ? 'Saving...'
                                : 'Mark lesson complete'}
                          </button>

                          {currentLessonIndex < activeModule.lessons.length - 1 ? (
                            <button
                              onClick={() => setCurrentLessonIndex((prev) => prev + 1)}
                              className="px-5 py-2 text-xs font-semibold rounded-lg bg-purple-600 text-white hover:bg-purple-700 shadow-sm"
                            >
                              Next Lesson →
                            </button>
                          ) : (
                            <button
                              onClick={() => setIsQuizMode(true)}
                              className="px-5 py-2 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm flex items-center space-x-1"
                            >
                              <span>Take Knowledge Check</span>
                              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Quiz Mode */}
                    {isQuizMode && (
                      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-6">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">
                              Module Evaluation
                            </span>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-indigo-100 text-indigo-800">
                              Pass threshold: 70%
                            </span>
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900">
                            Knowledge Check: {activeModule.title}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1">
                            Answer all {activeModule.quiz.length} questions based on ISO 22301 best practices to record your verified training completion.
                          </p>
                        </div>

                        {/* Quiz Results Banner */}
                        {quizResult && (
                          <div
                            className={`p-4 rounded-xl border ${
                              quizResult.passed
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                                : 'bg-amber-50 border-amber-300 text-amber-900'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                                  quizResult.passed ? 'bg-emerald-200 text-emerald-800' : 'bg-amber-200 text-amber-800'
                                }`}
                              >
                                {quizResult.passed ? '✓' : '!'}
                              </div>
                              <div>
                                <h4 className="font-bold text-sm">
                                  {quizResult.passed
                                    ? `Passed! Final Score: ${quizResult.score}%`
                                    : `Score: ${quizResult.score}% (70% Required)`}
                                </h4>
                                <p className="text-xs mt-0.5">{quizResult.message}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Questions List */}
                        <div className="space-y-6">
                          {activeModule.quiz.map((q, qIndex) => {
                            const resultItem = quizResult?.results.find((r) => r.id === q.id);

                            return (
                              <div
                                key={q.id}
                                className={`p-4 rounded-xl border transition-colors ${
                                  resultItem
                                    ? resultItem.is_correct
                                      ? 'border-emerald-300 bg-emerald-50/30'
                                      : 'border-red-300 bg-red-50/30'
                                    : 'border-gray-200 bg-white'
                                }`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-gray-900">
                                    {qIndex + 1}. {q.question}
                                  </h4>
                                  {resultItem && (
                                    <span
                                      className={`text-xs font-bold px-2 py-0.5 rounded ${
                                        resultItem.is_correct
                                          ? 'bg-emerald-100 text-emerald-800'
                                          : 'bg-red-100 text-red-800'
                                      }`}
                                    >
                                      {resultItem.is_correct ? 'Correct' : 'Incorrect'}
                                    </span>
                                  )}
                                </div>

                                {/* Options */}
                                <div className="space-y-2">
                                  {q.options.map((opt, optIndex) => {
                                    const isSelected = userAnswers[q.id] === optIndex;
                                    const isCorrect = resultItem?.correct_index === optIndex;

                                    let optionStyle =
                                      'border-gray-200 hover:bg-gray-50 text-gray-700';

                                    if (resultItem) {
                                      if (isCorrect) {
                                        optionStyle = 'border-emerald-500 bg-emerald-50 text-emerald-950 font-medium';
                                      } else if (isSelected && !resultItem.is_correct) {
                                        optionStyle = 'border-red-400 bg-red-50 text-red-950';
                                      } else {
                                        optionStyle = 'border-gray-200 opacity-60 text-gray-600';
                                      }
                                    } else if (isSelected) {
                                      optionStyle = 'border-purple-600 bg-purple-50 text-purple-950 font-medium';
                                    }

                                    return (
                                      <label
                                        key={optIndex}
                                        className={`flex items-start p-3 rounded-lg border cursor-pointer text-xs transition-colors ${optionStyle}`}
                                      >
                                        <input
                                          type="radio"
                                          name={`question-${q.id}`}
                                          checked={isSelected}
                                          disabled={quizSubmitting || (quizResult?.passed ?? false)}
                                          onChange={() => handleSelectAnswer(q.id, optIndex)}
                                          className="mt-0.5 mr-2.5 text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="flex-1">{renderFormattedText(opt)}</span>
                                      </label>
                                    );
                                  })}
                                </div>

                                {/* Explanation on completion */}
                                {resultItem && (
                                  <div className="mt-3 p-3 rounded-lg bg-gray-100 text-xs text-gray-700 border border-gray-200">
                                    <span className="font-bold text-gray-900">Explanation: </span>
                                    {resultItem.explanation}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Quiz Footer Actions */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <button
                            onClick={() => {
                              setIsQuizMode(false);
                              setCurrentLessonIndex(activeModule.lessons.length - 1);
                            }}
                            className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            ← Review Lessons
                          </button>

                          <div className="flex items-center space-x-3">
                            {quizResult ? (
                              quizResult.passed ? (
                                <button
                                  onClick={handleBackToCatalog}
                                  className="px-6 py-2.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                                >
                                  Return to Dashboard (Passed)
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    setQuizResult(null);
                                    setUserAnswers({});
                                  }}
                                  className="px-5 py-2.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                                >
                                  Retake Knowledge Check
                                </button>
                              )
                            ) : (
                              <button
                                onClick={handleSubmitQuiz}
                                disabled={quizSubmitting}
                                className="px-6 py-2.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-sm disabled:opacity-50 flex items-center space-x-1"
                              >
                                {quizSubmitting ? (
                                  <>
                                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                                    <span>Grading...</span>
                                  </>
                                ) : (
                                  <span>Submit Knowledge Check</span>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : (
                /* Dashboard Catalog Mode */
                <div className="space-y-6">
                  {/* Progress & Readiness Banner */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Overall Progress */}
                    <div className="bg-white border border-purple-100 rounded-xl p-5 shadow-sm col-span-2">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm">Curriculum Completion</h3>
                          <p className="text-xs text-gray-500">ISO 22301 Crisis Leadership Modules</p>
                        </div>
                        <span className="text-lg font-extrabold text-purple-700">
                          {overview ? `${overview.completed_modules} of ${overview.total_modules}` : '0 of 0'}
                        </span>
                      </div>
                      <div className="w-full bg-purple-100 rounded-full h-2.5 mb-3 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-purple-600 to-indigo-600 h-2.5 rounded-full transition-all duration-500"
                          style={{
                            width: `${overview ? overview.overall_progress_percent : 0}%`,
                          }}
                        ></div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>
                          {overview?.overall_progress_percent ?? 0}% completed · {overview?.completed_lessons ?? 0}/{overview?.total_lessons ?? 0} lessons
                        </span>
                        {overview?.average_score !== null && overview?.average_score !== undefined && (
                          <span className="font-semibold text-emerald-700">
                            Avg Score: {overview.average_score}%
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Certificate / Impact Pill */}
                    <div className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-xl p-5 text-white shadow-sm flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs uppercase font-bold tracking-wider text-purple-300">
                            Resilience Credential
                          </span>
                          <span className="text-xs">🏆</span>
                        </div>
                        <h4 className="font-bold text-sm">Crisis Leadership Certificate</h4>
                        <p className="text-xs text-purple-200 mt-1">
                          Verified credential for cyber insurance renewals and ISO 22301 audits.
                        </p>
                      </div>
                      <div className="mt-3">
                        {overview?.certificate_eligible ? (
                          <button
                            onClick={handleShowCertificate}
                            disabled={loadingCert}
                            className="w-full py-2 px-3 text-xs font-bold rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition-colors shadow flex items-center justify-center space-x-1"
                          >
                            {loadingCert ? (
                              <span>Loading...</span>
                            ) : (
                              <>
                                <span>View &amp; Print Certificate</span>
                                <svg className="w-3.5 h-3.5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </>
                            )}
                          </button>
                        ) : (
                          <div className="text-xs text-purple-300 bg-white/10 px-3 py-1.5 rounded-lg border border-white/10 text-center">
                            {Math.max(0, (overview?.total_modules ?? 0) - (overview?.completed_modules ?? 0))} more module
                            {(overview?.total_modules ?? 0) - (overview?.completed_modules ?? 0) === 1 ? '' : 's'} to unlock
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Modules Grid */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">
                      Training Modules ({overview?.modules.length ?? 0})
                    </h3>

                    {loading ? (
                      <div className="text-center py-12">
                        <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-xs text-gray-500">Loading modules...</p>
                      </div>
                    ) : error ? (
                      <div className="p-4 bg-red-50 text-red-700 text-xs rounded-xl border border-red-200">
                        {error}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(overview?.modules || []).map((mod: TrainingModuleSummary) => (
                          <div
                            key={mod.id}
                            className={`bg-white rounded-xl border p-5 transition-all hover:shadow-md flex flex-col justify-between ${
                              mod.is_completed
                                ? 'border-emerald-200 bg-emerald-50/20'
                                : 'border-gray-200'
                            }`}
                          >
                            <div>
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 border border-purple-100">
                                  {mod.category}
                                </span>
                                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                                  {mod.duration}
                                </span>
                              </div>

                              <h4 className="text-base font-bold text-gray-900 mb-1">
                                {mod.title}
                              </h4>
                              <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                                {mod.description}
                              </p>
                              <div className="text-xs text-gray-400 mb-4">
                                {mod.iso_clause} • {mod.lesson_count} Lessons • {mod.question_count}-Q Assessment
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                              <div>
                                {mod.is_completed ? (
                                  <div className="flex items-center space-x-1.5 text-emerald-700 text-xs font-bold">
                                    <svg className="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                                    </svg>
                                    <span>Completed ({mod.score}%)</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">
                                    {mod.lessons_completed > 0
                                      ? `${mod.lessons_completed}/${mod.total_lessons} lessons read`
                                      : 'Not started'}
                                  </span>
                                )}
                              </div>

                              <button
                                onClick={() => handleOpenModule(mod.id)}
                                className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                                  mod.is_completed
                                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-sm'
                                }`}
                              >
                                {mod.is_completed ? 'Review Module' : 'Start Module'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Printable Certificate Modal with z-[70] Stacking & Isolated Print Target */}
      {showCertModal && certificate && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm print-hide"
              onClick={() => setShowCertModal(false)}
            />
            <div
              id="printable-certificate-card"
              className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl p-8 border-8 border-amber-100"
            >
              {/* Certificate Inner Frame */}
              <div className="border-2 border-amber-600/40 p-8 rounded-xl text-center space-y-4 bg-gradient-to-b from-amber-50/30 to-white">
                <div className="flex justify-center items-center space-x-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-purple-800 to-indigo-800 flex items-center justify-center text-white text-xl font-extrabold shadow">
                    ⚡
                  </div>
                </div>

                <div className="uppercase tracking-widest text-xs font-bold text-amber-800">
                  Flash AI • Executive Academy of Business Continuity
                </div>

                <h1 className="text-3xl font-serif font-bold text-gray-900 tracking-wide">
                  Certificate of Crisis Leadership
                </h1>

                <p className="text-xs text-gray-500 uppercase tracking-wider">
                  This is proudly presented to
                </p>

                <div className="text-2xl font-bold text-purple-900 underline decoration-amber-400 underline-offset-8">
                  {certificate.recipient_name}
                </div>

                <p className="text-sm text-gray-700 max-w-lg mx-auto pt-2">
                  {certificate.company_name ? (
                    <>
                      of <strong>{certificate.company_name}</strong>{' '}
                    </>
                  ) : null}
                  for successfully completing all {certificate.total_modules} comprehensive training modules in Executive Crisis Leadership, Out-of-Band Incident Command, and Business Continuity aligned with <strong>{certificate.iso_standard}</strong> (Curriculum v{certificate.curriculum_version || '2026.1'}).
                </p>

                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-amber-200 text-xs text-gray-600 max-w-lg mx-auto">
                  <div>
                    <div className="font-bold text-gray-900">{certificate.issue_date}</div>
                    <div className="text-[10px] text-gray-400 uppercase">Date of Issuance</div>
                  </div>
                  <div>
                    <div className="font-bold text-emerald-700">{certificate.average_score}%</div>
                    <div className="text-[10px] text-gray-400 uppercase">Curriculum Score</div>
                  </div>
                  <div>
                    <a
                      href={`/verify/${certificate.verification_code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono font-bold text-indigo-600 hover:text-indigo-800 underline decoration-dotted"
                      title="Verify this credential on public verification page"
                    >
                      {certificate.verification_code}
                    </a>
                    <div className="text-[10px] text-gray-400 uppercase">Verification ID</div>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-between pt-6 print-hide">
                <button
                  onClick={() => setShowCertModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => window.print()}
                  className="px-6 py-2 text-xs font-bold rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-md flex items-center space-x-1.5"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span>Print Certificate</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Upgrade Modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        requiredFeature="premium"
        featureName="Executive Leadership Training"
      />
    </>
  );
};

export default ExecutiveTraining;
